import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Divider,
} from '@mui/material';
import {
  LocalHospital,
  CheckCircle,
  Pending,
  Cancel,
  Person,
  Phone,
  LocationOn,
  AccessTime,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { leadsService } from '../../services/leads';
import LoadingSpinner from '../common/LoadingSpinner';

const MyIPDRequests = ({ onRequestIPD, loading, onError }) => {
  const { user } = useSelector((state) => state.auth);
  const [myLeads, setMyLeads] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [requestDialog, setRequestDialog] = useState({ open: false, lead: null });

  useEffect(() => {
    loadMyLeads();
  }, []);

  const loadMyLeads = async () => {
    try {
      setLocalLoading(true);
      // Get leads assigned to current user with IPD-relevant statuses
      const response = await leadsService.getLeads({
        assigned_to: user.id,
        status: ['hot', 'warm', 'followup', 'pending', 'ipd_done'], // Include relevant statuses
        page_size: 50,
        ordering: '-updated_at'
      });

      setMyLeads(response.results || []);
    } catch (error) {
      console.error('Failed to load my leads:', error);
      onError('Failed to load your leads');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRequestIPD = async (lead) => {
    try {
      await onRequestIPD(lead.id);
      loadMyLeads(); // Refresh list
      setRequestDialog({ open: false, lead: null });
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const openRequestDialog = (lead) => {
    setRequestDialog({ open: true, lead });
  };

  const closeRequestDialog = () => {
    setRequestDialog({ open: false, lead: null });
  };

  const getStatusColor = (status) => {
    const colors = {
      'ipd_done': 'success',
      'pending': 'warning',
      'hot': 'error',
      'warm': 'info',
      'followup': 'secondary'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (lead) => {
    if (lead.status === 'ipd_done') {
      return <CheckCircle color="success" />;
    } else if (lead.status === 'pending' || lead.ipd_done_approval_status?.status === 'pending_approval') {
      return <Pending color="warning" />;
    }
    return <LocalHospital color="primary" />;
  };

  const canRequestIPD = (lead) => {
    // Can request IPD if status is not already ipd_done and no pending request
    return lead.status !== 'ipd_done' &&
           lead.ipd_done_approval_status?.status !== 'pending_approval';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase() || '??';
  };

  // Filter leads that are relevant for IPD requests
  const relevantLeads = myLeads.filter(lead =>
    ['hot', 'warm', 'followup', 'pending', 'ipd_done'].includes(lead.status) ||
    lead.ipd_done_approval_status?.status === 'pending_approval'
  );

  if (localLoading) {
    return <LoadingSpinner message="Loading your leads..." />;
  }

  if (relevantLeads.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <LocalHospital sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Leads Available for IPD Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Leads with status Hot, Warm, or Follow-up will appear here for IPD Done requests.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          My Leads - IPD Requests ({relevantLeads.length})
        </Typography>
        <Button
          variant="outlined"
          onClick={loadMyLeads}
          disabled={localLoading}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {relevantLeads.map((lead) => (
          <Grid item xs={12} md={6} lg={4} key={lead.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                {/* Patient Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {getInitials(lead.name)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" noWrap>
                      {lead.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {lead.phone}
                      </Typography>
                    </Box>
                  </Box>
                  {getStatusIcon(lead)}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Lead Details */}
                <Box sx={{ mb: 2 }}>
                  {lead.city_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>City:</strong> {lead.city_name}
                      </Typography>
                    </Box>
                  )}

                  {lead.disease_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalHospital fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Disease:</strong> {lead.disease_name}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTime fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <strong>Updated:</strong> {formatDate(lead.updated_at)}
                    </Typography>
                  </Box>
                </Box>

                {/* Status Information */}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={lead.status === 'pending' && lead.ipd_done_approval_status?.status === 'pending_approval'
                      ? 'IPD Pending Approval'
                      : lead.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(lead.status)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Lead #${lead.id}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                {/* IPD Status Details */}
                {lead.ipd_done_approval_status && (
                  <Alert
                    severity={
                      lead.ipd_done_approval_status.status === 'pending_approval' ? 'warning' :
                      lead.ipd_done_approval_status.status === 'approved' ? 'success' : 'info'
                    }
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="caption">
                      {lead.ipd_done_approval_status.status === 'pending_approval' &&
                        `IPD request pending approval (requested ${formatDate(lead.ipd_done_approval_status.requested_at)})`
                      }
                      {lead.ipd_done_approval_status.status === 'approved' &&
                        `IPD approved by ${lead.ipd_done_approval_status.approved_by} on ${formatDate(lead.ipd_done_approval_status.approved_at)}`
                      }
                    </Typography>
                  </Alert>
                )}
              </CardContent>

              {/* Action Button */}
              <Box sx={{ p: 2, pt: 0 }}>
                {lead.status === 'ipd_done' ? (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircle />}
                    fullWidth
                    disabled
                  >
                    IPD Completed
                  </Button>
                ) : lead.ipd_done_approval_status?.status === 'pending_approval' ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Pending />}
                    fullWidth
                    disabled
                  >
                    Pending Approval
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LocalHospital />}
                    onClick={() => openRequestDialog(lead)}
                    disabled={loading || !canRequestIPD(lead)}
                    fullWidth
                  >
                    Request IPD Done
                  </Button>
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Request IPD Confirmation Dialog */}
      <Dialog
        open={requestDialog.open}
        onClose={closeRequestDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request IPD Done Status</DialogTitle>
        <DialogContent>
          {requestDialog.lead && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are requesting to change the status of this lead to "IPD Done".
                This request will need manager approval.
              </Alert>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                  {getInitials(requestDialog.lead.name)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{requestDialog.lead.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {requestDialog.lead.phone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {requestDialog.lead.city_name} • {requestDialog.lead.disease_name}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Current Status: <Chip
                  label={requestDialog.lead.status.replace('_', ' ').toUpperCase()}
                  size="small"
                  color={getStatusColor(requestDialog.lead.status)}
                />
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRequestDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleRequestIPD(requestDialog.lead)}
            disabled={loading}
            startIcon={<LocalHospital />}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyIPDRequests;